/* SARAURA shared behaviour · vanilla, no dependencies.
   <script src="assets/sr.js" defer></script>
   Header hide/reveal · reveal-on-scroll · cart drawer. Nothing else. */
(function () {
  'use strict';

  var doc = document, root = doc.documentElement;
  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- 1. Header: toggles .is-unpinned / .is-scrolled --- */
  function initHeader() {
    var header = doc.querySelector('.sr-header');
    if (!header) return;
    var lastY = scrollY, ticking = false, held = 0;

    function update() {
      ticking = false;
      var y = scrollY, floor = header.offsetHeight + 40;
      header.classList.toggle('is-scrolled', y > 8);

      // Never hide with the drawer open, focus inside, near the top, or
      // mid in-page jump.
      if (root.classList.contains('sr-lock') || Date.now() < held ||
          header.contains(doc.activeElement) || y <= floor) {
        header.classList.remove('is-unpinned');
        lastY = y;
        return;
      }
      if (Math.abs(y - lastY) > 6) {  // ignore jitter
        header.classList.toggle('is-unpinned', y > lastY);
        lastY = y;
      }
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    // A jump to #lookbook scrolls down; that is not intent to hide.
    function hold() { held = Date.now() + 900; header.classList.remove('is-unpinned'); }

    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
    addEventListener('hashchange', hold);
    doc.addEventListener('click', function (e) {
      var a = e.target instanceof Element && e.target.closest('a[href*="#"]');
      if (a && a.hash && a.pathname === location.pathname) hold();
    }, true);
    update();
  }

  /* --- 2. Reveal on scroll ---
     Anything already in view is marked .is-in BEFORE .sr-js lands on <html>,
     so the hero and everything above the fold paints finished.

     CONTENT IS NEVER GATED BY THIS. The rise is decoration on top of a page
     that is already readable, and it is only ever allowed to hold an element
     back while the visitor is actively scrolling towards it. The moment that
     stops being true it gives up:

       · no JS, no IntersectionObserver, or reduce-motion  -> never hides at all
       · nobody has scrolled for IDLE ms                   -> resolve what is reached
       · tab hidden and restored                           -> resolve what is reached
       · anything reached still pending after CAP ms       -> resolve what is reached
       · print or find-in-page                             -> resolve EVERYTHING

     WHAT "REACHED" MEANS, AND WHY IT IS THE WHOLE FIX.
     Every one of those give-up paths used to resolve the entire document and
     then disconnect the observer, permanently. Measured on the home page at
     390px, scrolling at a human pace of 400px per 800ms down a 10,446px
     document: at t=7.2s, with the reader 31% of the way in, all 28 elements
     flipped opaque at once and the lookbook, the campaign grid and the brand
     story never animated again — for anyone, on any visit. Four and a half
     seconds of stillness anywhere did the same with no scrolling at all, and
     so did tabbing away and back.

     The guarantee that actually matters is that content can never be
     PERMANENTLY INVISIBLE. An element still below the viewport is not
     invisible content; it is unread. So the give-up paths now resolve only
     the elements the reader has reached — anything whose top edge is at or
     above the bottom of the viewport — and leave the observer connected for
     the rest. A screenshot, a crawler, a reader who never scrolls and a short
     window all still end up with every visible thing resolved, which is what
     those paths were for. Print is the exception and still takes the lot,
     because print lays out the whole document at once and every element is
     on the page. */
  function initReveal() {
    var items = doc.querySelectorAll('.sr-reveal');
    if (!items.length || reduced || !('IntersectionObserver' in window)) return;

    /* IDLE was 700ms, and that single number switched the whole effect off.

       Every visitor pauses on a hero for longer than seven tenths of a second
       — that is roughly the time it takes to read three words. So on a normal
       landing the timer fired before anyone scrolled, revealAll() ran, and it
       reveals with `transition: none`. By the time the page moved, all 28
       elements were already resolved. Measured on the home page at 390px:
       28 of 28 marked .is-in at load, 0 of them above the fold. The site had
       a reveal system that was never once seen working.

       The failsafe is worth keeping — content must never be stuck invisible
       behind an effect — but it has to be a failsafe, not a hair trigger.
       Four seconds of genuine stillness is someone who has stopped, not
       someone who is reading. And because both timers now resolve only what
       the reader has reached, neither of them can end the effect: they end
       the effect for the screen she is looking at, which is the only screen
       an idle timer can honestly have an opinion about. */
    var IDLE = 4000;   // real stillness, not a reading pause
    var CAP  = 9000;   // ceiling on anything already reached
    /* The CSS belt-and-braces (@keyframes sr-reveal-failsafe / sr-wipe-
       failsafe) is a fixed 15s from load, which on a twelve-screen document
       is still a clock the reader can lose a race to. It is meant to catch a
       script that threw, not a reader who is slow, so for as long as this
       script is demonstrably alive and something is still unread it pushes
       that expiry out ahead of itself. Stop pushing — because the script
       died, or because everything has resolved — and it lands 15s later. */
    var FAILSAFE = 15;    // seconds, and the fallback baked into the CSS
    var BEAT     = 4000;  // ms between pushes; well inside the 15s window

    var vh = innerHeight, pending = [], groups = new Map();

    items.forEach(function (el) {
      var p = el.parentElement || doc.body;  // stagger within own group
      var n = groups.get(p) || 0;
      groups.set(p, n + 1);
      el.style.setProperty('--sr-i', Math.min(n, 8));

      if (el.getBoundingClientRect().top < vh * 0.95) el.classList.add('is-in');
      else pending.push(el);
    });

    root.classList.add('sr-js');  // only now does the hidden state exist

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        io.unobserve(en.target);  // fires once
        var i = pending.indexOf(en.target);
        if (i > -1) pending.splice(i, 1);
        clearFailsafe(en.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

    pending.forEach(function (el) { io.observe(el); });

    var idleTimer, done = false, t0 = (window.performance || Date).now();

    /* Resolve a set of elements without playing the effect. This path is a
       fallback, not a performance: elements land finished rather than fading
       up in a block.

       An inline style only reaches the element itself, and the motion layer
       puts the photograph wipe on that element's *child* frame — out of reach
       of this. So the same instruction is also published as a class on
       <html>, which sr-motion.css reads (§06). Without it, giving up would
       play thirteen 1.2s wipes at once into a room nobody is looking at,
       which is the opposite of what giving up means. */
    function settle(list) {
      if (!list.length) return;
      root.classList.add('sr-revealed-all');
      list.forEach(function (el) {
        el.style.transition = 'none';
        el.classList.add('is-in');
        io.unobserve(el);
        clearFailsafe(el);
      });
      // Hand the transition back once the frame has committed, so anything
      // re-observed later still animates normally.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          list.forEach(function (el) { el.style.transition = ''; });
          root.classList.remove('sr-revealed-all');
        });
      });
    }

    /* THE GIVE-UP PATH, SCOPED. Everything the reader has actually reached —
       top edge at or above the bottom of the window — resolves; everything
       below it stays with the observer, because it is unread, not hidden.
       The observer is never disconnected and the listeners are never removed,
       so the effect survives its own fallback. */
    function revealReached() {
      clearTimeout(idleTimer);
      if (done) return;
      var vhNow = innerHeight, reached = [], rest = [];
      pending.forEach(function (el) {
        // Anything the observer has already taken is simply dropped.
        if (el.classList.contains('is-in')) return;
        (el.getBoundingClientRect().top <= vhNow ? reached : rest).push(el);
      });
      pending = rest;
      settle(reached);
    }

    /* Print and find-in-page take the whole document: print lays out every
       element at once, so nothing is below anything. This is the one path
       that ends the effect, and it is the correct one to end it. */
    function revealEverything() {
      if (done) return;
      done = true;
      clearTimeout(idleTimer);
      clearInterval(beat);
      var all = pending;
      pending = [];
      settle(all);
      io.disconnect();
    }

    function arm() {
      if (done) return;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(revealReached, IDLE);
    }

    /* Proof of life for the CSS expiry. While anything is still unread, push
       the failsafe delay ahead of the wall clock; the animation has not
       started yet, so raising its delay simply moves the start. Every four
       seconds, and only while there is something left to protect — no
       geometry is read and nothing is measured here. When pending empties, or
       when this script stops running for any reason at all, the last value
       written expires 15s later and every remaining element resolves itself
       with no script involved.

       WRITTEN ON THE PENDING ELEMENTS, NOT ON <html>. The first version set
       one custom property on the root, which is a one-line push and a
       whole-document style invalidation: measured at 4x CPU throttle on the
       home page, exactly one frame per beat blew out to 50ms — 116ms on the
       first — against a 16.7ms median with the beat off. Nothing else on this
       page costs a frame; a failsafe certainly must not. Inline animation-
       delay on the two dozen elements that actually carry the animation, plus
       the frames inside the ones that hold the wipe, invalidates those and
       nothing else. Re-measured: median 16.7ms, max 16.8ms, zero frames over
       33ms, beat running. */
    var FRAME_SEL = '.sr-media, .sr-tile__media, .sr-card__media, .pd-stage';
    function eachFailsafeNode(el, fn) {
      fn(el);
      var kids = el.querySelectorAll(FRAME_SEL);
      for (var i = 0; i < kids.length; i++) fn(kids[i]);
    }
    function clearFailsafe(el) {
      eachFailsafeNode(el, function (n) { n.style.animationDelay = ''; });
    }
    var beat = setInterval(function () {
      if (done || !pending.length) { clearInterval(beat); return; }
      var v = (((window.performance || Date).now() - t0) / 1000 + FAILSAFE).toFixed(1) + 's';
      pending.forEach(function (el) {
        if (el.classList.contains('is-in')) return;
        eachFailsafeNode(el, function (n) { n.style.animationDelay = v; });
      });
    }, BEAT);

    addEventListener('scroll', arm, { passive: true });
    addEventListener('resize', arm, { passive: true });
    addEventListener('beforeprint', revealEverything);
    // Restoring a background tab must not land on blank space — but it must
    // not blank the rest of the page's motion either, so it resolves the
    // screen she is looking at and nothing else.
    doc.addEventListener('visibilitychange', function () {
      if (!doc.hidden) { revealReached(); arm(); }
    });
    if (matchMedia) {
      var mq = matchMedia('print');
      if (mq && mq.addEventListener) {
        mq.addEventListener('change', function (e) { if (e.matches) revealEverything(); });
      }
    }

    arm();
    setTimeout(revealReached, CAP);
  }

  /* --- 2b. Rail cards on a touch screen ---
     A product card in the rail carries two photographs, and until now the
     second one only ever appeared on hover. On a phone there is no hover, so
     on every touch device that image was downloaded and never once seen.

     Same gesture, different input: whichever card is standing in the middle
     of the rail is the one showing its second photograph. It arrives by
     scrolling and it leaves by scrolling, on the same 700ms cross-fade a
     mouse gets. Not a tap state — a tap opens the product, and nothing in
     this house jumps when you touch it.

     The observer is rooted on the rail and its side margins cut the root
     down to the middle 16% of the track, so "in the middle" is decided by
     the compositor. Nothing here reads geometry, runs on scroll, or costs a
     frame. Presentation only: sr-motion.css owns what .is-poised looks like,
     and a page without that file is simply unaffected. */
  function initRailPoise() {
    if (reduced || !('IntersectionObserver' in window)) return;
    if (!matchMedia('(hover: none)').matches) return;

    doc.querySelectorAll('.rail').forEach(function (rail) {
      var cards = [].slice.call(rail.querySelectorAll('.sr-card'));
      var withAlt = cards.filter(function (c) {
        return c.querySelector('.sr-card__img--alt');
      });
      if (!cards.length) return;
      /* ALL OR NOTHING, AND THIS IS A CONTENT GAP RATHER THAN A CSS FAULT.
         Measured on the eight-card home rail: only cards 1 and 8 carry a
         second photograph, so the gesture fired on two of eight and the other
         six simply sat there. A system that runs on a quarter of a row does
         not read as a system, it reads as a rendering inconsistency — worse
         than not having it. So it runs on a rail where every card can answer,
         and stands down on one where they cannot.
         OWNER: supply a second photograph for the six middle cards and this
         turns itself back on with no code change. */
      if (withAlt.length !== cards.length) return;
      cards = withAlt;

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          en.target.classList.toggle('is-poised', en.isIntersecting);
        });
      }, { root: rail, rootMargin: '0px -42% 0px -42%', threshold: 0.01 });

      cards.forEach(function (c) { io.observe(c); });
    });
  }

  /* --- 3. Cart drawer ---
     Open: [data-sr-drawer-open="sr-cart"]. The .sr-drawer needs id,
     role="dialog", aria-modal, aria-label, tabindex="-1".
     Close: [data-sr-drawer-close], the scrim, or Escape. */
  function initDrawer() {
    var open = null, restore = null;
    var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
                    'select,textarea,[tabindex]:not([tabindex="-1"])';

    function lock(on) {
      var sbw = innerWidth - root.clientWidth;
      root.style.setProperty('--sr-sbw', on && sbw > 0 ? sbw + 'px' : '0px');
      root.classList.toggle('sr-lock', on);
    }
    function shut(el, off) {
      el.classList.toggle('is-open', !off);
      if (off) el.setAttribute('aria-hidden', 'true'); else el.removeAttribute('aria-hidden');
      if ('inert' in el) el.inert = off;
      doc.querySelectorAll('[data-sr-drawer-open="' + el.id + '"]').forEach(
        function (b) { b.setAttribute('aria-expanded', String(!off)); });
    }

    function openDrawer(el) {
      if (!el || open) return;
      open = el;
      restore = doc.activeElement;
      shut(el, false);
      lock(true);
      var t = el.querySelector('[data-sr-autofocus]') ||
              el.querySelector('.sr-drawer__close') || el;
      requestAnimationFrame(function () { t.focus({ preventScroll: true }); });
    }

    function closeDrawer() {
      if (!open) return;
      var el = open;
      open = null;
      // Focus must leave before the panel goes inert, or it falls to <body>.
      if (restore && restore.focus) restore.focus({ preventScroll: true });
      restore = null;
      shut(el, true);
      lock(false);
    }

    doc.querySelectorAll('.sr-drawer').forEach(function (el) {
      if (!el.classList.contains('is-open')) shut(el, true);
    });

    doc.addEventListener('click', function (e) {
      if (!(e.target instanceof Element)) return;
      var o = e.target.closest('[data-sr-drawer-open]');
      if (o) {
        e.preventDefault();
        openDrawer(doc.getElementById(o.getAttribute('data-sr-drawer-open')));
      } else if (e.target.closest('[data-sr-drawer-close]') ||
                 e.target.classList.contains('sr-drawer__scrim')) {
        e.preventDefault();
        closeDrawer();
      }
    });

    /* THE TRAP. Two escapes this used to leave open, both of them live in
       the drawer's DEFAULT state — an empty bag, which is every first visit.

       1. A hidden control counted as a stop. An empty bag still carries a
          display:none "Checkout" + "Continue shopping" pair in the DOM, so
          `last` was a phantom node that can never be document.activeElement,
          the wrap-around never fired, and Tab walked straight out of an
          aria-modal dialog onto the obscured page behind the scrim. Measured
          on product.html: Close bag -> View the collection -> BODY -> Skip to
          content -> Menu -> ... Shift+Tab leaked identically. Adding an item
          took the hidden block out of the DOM and the trap started working,
          which is why it read as sound.
       2. Focus sitting on the dialog container itself — which is where
          openDrawer() puts it when the panel has no close button and `t`
          falls through to `el`. That node is neither `first` nor `last`, so
          Shift+Tab walked out of the back of the box.

       Both are fixed the same way product.html's own dialog fixed them forty
       lines from here: count only controls that are actually rendered, and
       re-enter from wherever focus really is. One change, four pages. */
    doc.addEventListener('keydown', function (e) {
      if (!open) return;
      if (e.key === 'Escape') return closeDrawer();
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(
        open.querySelectorAll(FOCUSABLE), function (el) {
          return el.offsetWidth || el.offsetHeight || el.getClientRects().length;
        });
      if (!f.length) { e.preventDefault(); open.focus(); return; }
      var first = f[0], last = f[f.length - 1], a = doc.activeElement;
      if (!open.contains(a)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && (a === first || a === open)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
    });

    window.SR = { openDrawer: function (id) { openDrawer(doc.getElementById(id)); },
                  closeDrawer: closeDrawer };
  }

  /* --- 4. A link inside a drawer closes it before it navigates --- */
  function initDrawerLinks() {
    doc.querySelectorAll('.sr-drawer a[href]').forEach(function (a) {
      a.addEventListener('click', function () {
        if (window.SR && window.SR.closeDrawer) window.SR.closeDrawer();
      });
    });
  }

  /* --- 5. Footer newsletter ---
     Nothing receives the address yet, so the form does not claim to have
     taken it. It answers with the one route that does reach a person, which
     is an action rather than an apology. Lives here so every page behaves
     alike. */
  function initNewsletter() {
    var form = doc.getElementById('nl-form');
    var note = doc.getElementById('nl-note');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (note) {
        note.innerHTML = '<a class="sr-link sr-link--meta" href="about.html">' +
                         'Ask us to add you</a>';
      }
    });
  }

  /* --- 6. Warm the lazy photographs once the page is quiet ---
     Every photograph below the fold is loading="lazy", which is right for the
     critical path and wrong for everything that happens afterwards: a lazy
     image only ever loads if something scrolls towards it, so printing the
     page, capturing it, or reading it in a window tall enough to show several
     sections at once all produce a document full of empty frames. On a shop
     whose entire content is photographs that is the same defect as hiding the
     text.

     So: hero and first-screen images load normally, and once the page has
     finished loading and gone idle, the rest are promoted to eager a few at a
     time, in document order, well behind the LCP image. The visitor gets a
     rail that is already filled in by the time they reach it instead of a row
     of grey boxes, and any consumer that never scrolls still ends up with the
     whole thing.

     Skipped entirely on Save-Data or a 2G-class connection, where the original
     lazy behaviour is the correct one and grey boxes are the lesser evil. */
  function initImageWarm() {
    /* NOT the navigation takeover's five category tiles. They are 374 KB of
       photograph inside a visibility:hidden panel, and this warm was fetching
       every one of them on every page load whether or not the visitor ever
       opened the menu — on the product page that was a third of the document's
       total bytes spent on something behind a closed door. They already have a
       warm of their own in sr-nav.js, bound to first intent on the menu
       trigger, which starts the fetch a beat before the panel is on screen and
       is the correct trade for a panel nobody has asked for yet. Everything in
       the document itself — gallery frames 2-4, the thumbnails, the
       recirculation rail — still warms exactly as before. */
    var imgs = [].slice.call(doc.querySelectorAll('img[loading="lazy"]:not(.srn-prev__img)'));
    if (!imgs.length) return;

    /* decoding="async" is what lets the first paint happen without waiting on
       any photograph, and it is worth keeping — dropping it costs about 175ms
       of first contentful paint here. The price is that a decode only happens
       when something actually needs to rasterise that image, so a print or a
       full-page capture can come out with the frames empty even though every
       file is loaded. Asking for the decode ourselves, off the main thread and
       only once the page is idle, buys back the finished bitmap without giving
       up the fast paint. */
    function decode(img) {
      if (typeof img.decode !== 'function') return;
      img.decode().catch(function () {});   // a decode we never needed is free
    }

    function promoteAll() {
      imgs.forEach(function (img) { img.loading = 'eager'; decode(img); });
      imgs = [];
    }

    // Print must never go to paper with holes in it, whatever the connection.
    addEventListener('beforeprint', promoteAll);
    if (matchMedia) {
      var mq = matchMedia('print');
      if (mq && mq.addEventListener) {
        mq.addEventListener('change', function (e) { if (e.matches) promoteAll(); });
      }
    }

    var c = navigator.connection;
    if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return;

    function warm() {
      var i = 0;
      (function next() {
        if (i >= imgs.length) return;
        for (var n = 0; n < 6 && i < imgs.length; n++, i++) {
          imgs[i].loading = 'eager';
          decode(imgs[i]);
        }
        setTimeout(next, 90);
      })();
    }

    function start() {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(warm, { timeout: 400 });
      } else {
        setTimeout(warm, 300);
      }
    }

    if (doc.readyState === 'complete') start();
    else addEventListener('load', start, { once: true });
  }

  function boot() {
    initReveal(); initRailPoise(); initHeader(); initDrawer(); initDrawerLinks();
    initNewsletter(); initImageWarm();
  }
  if (doc.readyState !== 'loading') boot();
  else doc.addEventListener('DOMContentLoaded', boot, { once: true });
})();
